import React, { useMemo, useCallback } from 'react';
import { makeModalThreeLOPromptForCustomUI } from '@atlassian/forge-ui';
import {
	CONTEXT_TOKEN_TYPE_JIRA,
	ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
} from '@atlassian/jira-forge-ui-constants';
import { LazyIframe } from '@atlassian/jira-forge-ui-iframe-generic';
import type {
	IssueViewBackgroundScriptModule,
	IssueViewBackgroundScriptExtensionData,
	CustomUIComponentMap,
	ExtensionNextProps,
	IssueViewBackgroundScript,
} from '@atlassian/jira-forge-ui-types';
import { isCustomUIExtension } from '@atlassian/jira-forge-ui-utils-internal/src/utils/extension';
import Placeholder from '@atlassian/jira-placeholder';
import { InvisibleDiv } from '../../common/ui/invisible-div';
import type { LazyProps } from '../../types';

type Props = ExtensionNextProps<
	IssueViewBackgroundScriptModule,
	IssueViewBackgroundScript,
	IssueViewBackgroundScriptExtensionData
>;

type PropsWithTestId = Props & { testId?: string };

export const AsyncIssueViewBackgroundScript = (props: LazyProps<PropsWithTestId>) => {
	const extensionData = useMemo(
		() => ({
			...props.extensionData,
			type: ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
		}),
		[props.extensionData],
	);

	const components = useCallback(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				ThreeLOPrompt: makeModalThreeLOPromptForCustomUI({
					// Replace with lodash/noop
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onClose: () => {},
					appName: props.extension.properties.title,
				}),
			}) as CustomUIComponentMap,
		[props.extension.properties.title],
	);

	if (!isCustomUIExtension(props.extension)) return null;

	return (
		<InvisibleDiv>
			<Placeholder name="issue-view-background-script" fallback={null}>
				<LazyIframe
					{...props}
					module={ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE}
					extensionData={extensionData}
					components={components}
					contextToken={CONTEXT_TOKEN_TYPE_JIRA}
				/>
			</Placeholder>
		</InvisibleDiv>
	);
};
